import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Download, Eye, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function FormResponses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/forms/${id}/responses`);
      setRows(data.items); setName(data.form_name);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const view = async (rid) => {
    try { const { data } = await api.get(`/forms/${id}/responses/${rid}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/forms/${id}/responses/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${name || "form"}_responses.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="form-responses">
      <button onClick={() => navigate("/admin/forms")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#006090]" data-testid="responses-back">
        <ChevronLeft className="w-4 h-4" /> Forms
      </button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{name} — Responses</h1>
          <p className="text-sm text-slate-500 mt-0.5">{rows.length} submission{rows.length === 1 ? "" : "s"}</p>
        </div>
        {rows.length > 0 && <Button variant="outline" onClick={exportCsv} data-testid="responses-export"><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl" data-testid="responses-empty">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium text-slate-600">No responses yet</p>
          <p className="text-sm text-slate-400 mt-1">Submissions will appear here once the form is used.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left px-4 py-2.5">Submitted</th><th className="text-left px-4 py-2.5">By</th><th className="text-left px-4 py-2.5">Version</th><th className="text-left px-4 py-2.5">Answers</th><th className="px-4 py-2.5"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`response-row-${r.id}`}>
                  <td className="px-4 py-2.5 text-slate-700">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.submitted_by_name || "—"}</td>
                  <td className="px-4 py-2.5"><Badge variant="secondary">v{r.form_version}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-500">{r.answer_count}</td>
                  <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" onClick={() => view(r.id)} data-testid={`response-view-${r.id}`}><Eye className="w-4 h-4 mr-1" />View</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="response-detail">
          <DialogHeader><DialogTitle>Response</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              {(detail.questions || []).map((q) => {
                const a = detail.answers?.[q.id];
                return (
                  <div key={q.id}>
                    <p className="text-xs font-medium text-slate-500">{q.label}</p>
                    <p className="text-sm text-slate-800">{Array.isArray(a) ? a.join(", ") : (a || "—")}</p>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
